import { useRef, useState } from "react";
import { ChevronDown, Presentation } from "lucide-react";
import logo from "@/assets/logo.svg";
import { AccountMenu } from "@/auth/AccountMenu";
import { DownloadDesktopApp } from "@/components/DownloadDesktopApp";
import { useAuth } from "@/auth/useAuth";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DeckSwitcher } from "@/components/deck/DeckSwitcher";
import { DECK_FILE_EXTENSION } from "@/lib/deckFile";
import { isElectron } from "@/lib/runtimeEnv";

interface TopBarProps {
  // Enter fullscreen presentation (TODO §5.6). Exit is driven from within the overlay
  // (Esc / browser fullscreen-exit), so the bar only ever needs an "enter" action.
  onPresent: () => void;
  // Enter print mode and open the browser's print-to-PDF dialog (TODO §6.2).
  onExportPdf: () => void;
  // Export the deck as a single self-contained `.html` file (HTML export).
  onExportHtml: () => void;
  // Export the deck as a `.deckai` archive — the backup/interchange format (§12.6).
  onExportDeckFile: () => void;
  // This page does not hold the deck's write lock (§12.5.2): the title is not editable here.
  readOnly?: boolean;
}

export function TopBar({
  onPresent,
  onExportPdf,
  onExportHtml,
  onExportDeckFile,
  readOnly = false,
}: TopBarProps) {
  const { status, promptSignIn } = useAuth();

  // §9.4 Export requires login. The reminder popup; the export that's waiting behind it.
  const [gateOpen, setGateOpen] = useState(false);
  const pendingExportRef = useRef<(() => void) | null>(null);

  // §9.4.1 Gate an export action. Signed in → run it. Signed out → hold it and show the
  // friendly reminder instead of exporting. Editing stays fully available either way (§9.4.3).
  //
  // §14.6.6 The desktop shell is exempt, in or out of the offline window. This gates the
  // user's own deck to their own disk with no server anywhere in the loop; a hard wall in an
  // app that is explicitly built to work at 38,000 feet is user-hostile, and trivially
  // bypassed on a machine they own besides. The web gate is unchanged.
  const gatedExport = (run: () => void) => {
    if (status === "in" || isElectron()) {
      run();
      return;
    }
    pendingExportRef.current = run;
    setGateOpen(true);
  };

  // Reminder → "Sign in": open the shared §9.2 dialog and, on success, resume the held
  // export so the user isn't made to click twice (§9.4.2).
  const handleGateSignIn = () => {
    const run = pendingExportRef.current;
    pendingExportRef.current = null;
    promptSignIn(run ?? undefined);
  };

  // Any other close (Cancel / Esc / overlay) abandons the export.
  const handleGateOpenChange = (open: boolean) => {
    if (!open) pendingExportRef.current = null;
    setGateOpen(open);
  };

  return (
    <header className="flex items-center gap-6 bg-slate-900 px-6 py-3 text-white">
      <div className="flex items-center gap-8">
        <img src={logo} alt="DeckAI" className="h-8 w-auto" />
        <nav className="flex items-center gap-6 text-sm">
          <a
            href="https://www.griddtemplates.com/#pricing"
            target="blank"
            className="hover:text-slate-300"
          >
            Get PowerPoint Add-in
          </a>
        </nav>
      </div>
      {/* §12.4.2 Title + the document switcher. Picking another deck navigates to
          `/d/:deckId`; it never swaps decks in place. */}
      <div className="flex flex-1 justify-center">
        <DeckSwitcher readOnly={readOnly} />
      </div>
      <div className="flex items-center gap-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" size="sm" className="gap-2">
              Export <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {/* Both exports gated behind login (§9.4): PDF via the browser print dialog
                (TODO §6.2); HTML as a single self-contained file (§6.6). */}
            <DropdownMenuItem onClick={() => gatedExport(onExportPdf)}>
              PDF
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => gatedExport(onExportHtml)}>
              HTML
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {/* §12.6.1 Deliberately NOT behind the sign-in gate: this is the only backup a
                browser-only user has, and holding someone's data hostage to an account is
                not a thing to do. PDF/HTML are published artefacts; this is their own deck. */}
            <DropdownMenuItem onClick={onExportDeckFile}>
              Deck file ({DECK_FILE_EXTENSION})
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          variant="secondary"
          size="sm"
          className="gap-2"
          onClick={onPresent}
        >
          <Presentation className="h-4 w-4" />
          Presentation mode
        </Button>
        {/* §14.11.4 Website only — the component removes itself inside the shell. */}
        <DownloadDesktopApp />
        {/* §9.3 Far-right account control: skeleton while auth loads, then Sign in / avatar. */}
        <AccountMenu />
      </div>

      {/* §9.4 Friendly export-requires-login reminder. Not a hard wall — it only intercepts
          the export; the deck stays fully editable signed-out (§9.4.3). */}
      <AlertDialog open={gateOpen} onOpenChange={handleGateOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sign in to export your deck</AlertDialogTitle>
            <AlertDialogDescription>
              Exporting requires a free account. Sign in to download your deck —
              your work stays right here in the meantime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not now</AlertDialogCancel>
            <AlertDialogAction onClick={handleGateSignIn}>
              Sign in
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </header>
  );
}
